import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from './AuthContext';
import { usersAPI } from '../services/api';

const ThemeContext = createContext(null);

const DARK_LOGO = "https://customer-assets.emergentagent.com/job_moneyswap-9/artifacts/pvoha9c5_D%24.png";
const LIGHT_LOGO = "https://customer-assets.emergentagent.com/job_moneyswap-9/artifacts/pvoha9c5_D%24.png";

export function ThemeProvider({ children }) {
  const { user } = useAuth();
  const [theme, setThemeState] = useState(() => {
    return localStorage.getItem('divispend-theme') || 'dark';
  });

  useEffect(() => {
    if (user?.theme) {
      setThemeState(user.theme);
      localStorage.setItem('divispend-theme', user.theme);
    }
  }, [user?.theme]);

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
    localStorage.setItem('divispend-theme', theme);
  }, [theme]);

  const setTheme = useCallback(async (newTheme) => {
    setThemeState(newTheme);
    localStorage.setItem('divispend-theme', newTheme);
    if (user) {
      try {
        await usersAPI.updateProfile({ theme: newTheme });
      } catch (err) {
        console.error('Failed to save theme preference:', err);
      }
    }
  }, [user]);

  const toggleTheme = useCallback(() => {
    setTheme(theme === 'dark' ? 'light' : 'dark');
  }, [theme, setTheme]);

  const isDark = theme === 'dark';
  const logo = isDark ? DARK_LOGO : LIGHT_LOGO;

  const t = useMemo(() => ({
    // Legacy tokens (kept for existing consumers)
    bg: isDark ? '#111111' : '#FFFFFF',
    bgSecondary: isDark ? '#1A1A1A' : '#F5F5F7',
    border: isDark ? '#333333' : '#E5E5E5',
    text: isDark ? '#FFFFFF' : '#111111',
    textSecondary: isDark ? '#999999' : '#555555',
    textMuted: isDark ? '#666666' : '#999999',
    cardBg: isDark ? '#1A1A1A' : '#FFFFFF',
    headerBg: '#111111',
    inputBg: isDark ? '#111111' : '#FFFFFF',
    hoverBg: isDark ? '#333333' : '#F5F5F7',

    // New dashboard design tokens (2026 refresh)
    pageBg: isDark ? '#000000' : '#F8FAFC',
    surface: isDark ? '#121212' : '#FFFFFF',
    surfaceAlt: isDark ? '#161616' : '#F1F5F9',
    surfaceHover: isDark ? '#191919' : '#F8FAFC',
    borderSubtle: isDark ? 'rgba(0, 229, 117, 0.15)' : '#E2E8F0',
    borderHover: 'rgba(0, 229, 117, 0.45)',
    hoverGlow: '0 0 28px -12px rgba(0, 229, 117, 0.45)',
    textPrimary: isDark ? '#FFFFFF' : '#0F172A',
    textMutedNew: isDark ? 'rgba(255,255,255,0.55)' : '#64748B',
    textFaint: isDark ? 'rgba(255,255,255,0.4)' : '#94A3B8',
    accent: '#00E575',
    accentSoft: isDark ? 'rgba(0, 229, 117, 0.15)' : 'rgba(0, 229, 117, 0.12)',
    accentText: isDark ? '#00E575' : '#059669', // WCAG-safe on light bg
    inputBgNew: isDark ? '#161616' : '#FFFFFF',
    inputBorderNew: isDark ? '#1E1E1E' : '#CBD5E1',
    dividerBorder: isDark ? '#1E1E1E' : '#E2E8F0',
    headerBgNew: isDark ? 'rgba(10,10,10,0.85)' : 'rgba(255,255,255,0.85)',
    headerBorder: isDark ? '#1a1a1a' : '#E2E8F0',
  }), [isDark]);

  const value = useMemo(() => ({
    theme, setTheme, toggleTheme, isDark, logo, t
  }), [theme, setTheme, toggleTheme, isDark, logo, t]);

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
}
